import { InternalTransferSubmit, RegularISubmit } from '@interfaces';
import {
  MultiSignerPostPayload,
  MXMPosition,
  MXMTaxLot,
} from '@interfaces/models/multi-signer-approval';
import { createSelector } from '@ngrx/store';
import {
  AccountTypes,
  FlowTypes,
  ISelectedAccount,
  IUserInputAccount,
  YesNoEmpty,
} from '@shared/models';
import { CostBasisPosition } from '@store/reducers/cost-basis/cost-basis.reducer';
import { GiftingState } from '@store/reducers/gifting/gifting.reducer';
import { InternalTransferState } from '@store/reducers/internal-transfer/internal-transfer.reducer';
import * as fromMXMState from '@store/reducers/mxm/mxm.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import { getTaxLotsAmount, getTotalAmountForPosition } from 'sharedUtils/utils.helpers';

import { getMXMState } from '../../reducers/index';
import { selectSelectedPositions } from '../cost-basis/cost-basis.selectors';
import { selectGiftingState } from '../gifting/gifting.selectors';
import { selectInternalTransferState } from '../internal-transfer/internal-transfer.selectors';
import { selectSubmitMetricsID } from '../open-account/open-account.selectors';
import { selectedAccountsFrom } from '../selected-client-accounts/selected-client-accounts.selectors';
import { getITSubmitPayload, getSubmitPayload } from '../submit/submit.selectors';
import { selectTriageState } from '../triage/triage.selectors';

export const selectMXMState = createSelector(getMXMState, (state) => state);
export const selectMXMApprovalStatus = createSelector(
  selectMXMState,
  fromMXMState.getMXMApprovalStatus,
);
export const selectMXMTransferId = createSelector(selectMXMState, fromMXMState.getMXMTransferId);
export const selectMXMSignState = createSelector(selectMXMState, fromMXMState.getMXMSignState);
export const selectMXMTransferStatus = createSelector(
  selectMXMState,
  fromMXMState.getMXMTransferStatus,
);

export const selectIsFinalSigner = createSelector(selectMXMState, fromMXMState.getIsFinalSigner);

export const getMultiSignerPostPayload = createSelector(
  selectTriageState,
  selectGiftingState,
  selectInternalTransferState,
  selectedAccountsFrom,
  selectSelectedPositions,
  getITSubmitPayload,
  getSubmitPayload,
  selectSubmitMetricsID,
  (
    triageState,
    giftingState,
    ITState,
    [account],
    selectedPositions,
    itSubmitPayload,
    submitPayload,
    metricsCorrelationID,
  ): MultiSignerPostPayload => {
    try {
      const { flowType } = triageState;

      switch (flowType) {
        case FlowTypes.INTERNAL_TRANSFER:
          return getInternalTransferPayload(
            ITState,
            selectedPositions,
            itSubmitPayload,
            metricsCorrelationID,
          );

        case FlowTypes.GIFTING:
          return getGiftingPayload(
            giftingState,
            triageState,
            account,
            selectedPositions,
            submitPayload,
            metricsCorrelationID,
          );

        default:
          throw new Error('Invalid Flow Type');
      }
    } catch (e) {
      //for whatever reason, catching this works. I think it's because this selector is constructed of
      //many selectors that point at slices of state that constantly get updated, such that this thing errors out bc it's missing data whenever those slices get updated
    }
  },
);

function getInternalTransferPayload(
  state: InternalTransferState,
  selectedPositions: CostBasisPosition[],
  itPayload: InternalTransferSubmit,
  metricsCorrelationID: string,
): MultiSignerPostPayload {
  const from = state.transferFrom;
  const fromAccount = from.selectedAccount.accountData;
  const to = state.transferTo.selectedAccount;
  const triage = state.triage;
  return {
    triage: {
      transferFrom: triage.transferFrom,
      transferTo: triage.transferTo,
      isOwner: triage.isOwner,
      exceptionsApply: triage.exceptionsApply,
      flowType: FlowTypes.INTERNAL_TRANSFER,
    },
    transferFrom: {
      accountName: fromAccount.accountName,
      accountNumber: fromAccount.accountNumber,
      accountType: fromAccount.accountType,
      accountId: fromAccount.accountId,
      transactionType: state.transactionType,
      transferType: from.transferType,
      transferMethod: from.transferMethod,
      percentageOfHoldings: +from.percentageOfHoldings,
      convertAvgCostHoldings: from.convertAvgCostHoldings,
      convertSpecIDHoldings: from.convertSpecIDHoldings,
    },
    transferTo: {
      accountNumber: to.accountNumber,
      accountName:
        (to as IUserInputAccount).accountName || (to as ISelectedAccount).accountData.accountName,
    },
    positions: getPositionPayload(selectedPositions, FlowTypes.INTERNAL_TRANSFER),
    metricsCorrelationID,
    filenet: itPayload,
  };
}

function getGiftingPayload(
  giftingState: GiftingState,
  triageState: TriageState,
  account: ISelectedAccount,
  selectedPositions: CostBasisPosition[],
  submitPayload: RegularISubmit,
  metricsCorrelationID: string,
): MultiSignerPostPayload {
  const { recipient } = giftingState;
  return {
    triage: {
      transferFrom: triageState.accountTypeInheriting,
      transferTo: AccountTypes.EMPTY,
      flowType: FlowTypes.GIFTING,
      giftingDesignation: triageState.giftingDesignation,
    },
    transferFrom: {
      accountNumber: account.accountNumber,
      accountType: triageState.accountTypeInheriting,
      accountName: account.accountData.accountName.split(' — ')[0],
      accountId: account.accountData.accountId,
    },
    transferTo: {
      accountNumber: recipient.accountNumber,
      accountName: recipient.name,
      giftRecipientType: recipient.type,
      anonymousGift: recipient.anonymousGift === YesNoEmpty.YES ? YesNoEmpty.YES : YesNoEmpty.NO,
      dtcNumber: recipient.dtcNumber,
      firm: recipient.firm,
    },
    positions: getPositionPayload(selectedPositions, FlowTypes.GIFTING),
    metricsCorrelationID,
    filenet: submitPayload,
  };
}

const getPositionPayload = (
  selectedPositions: CostBasisPosition[],
  flowType: FlowTypes,
): MXMPosition[] => {
  return selectedPositions.map((position) => {
    const payloadPosition: MXMPosition = {
      positionId: position.positionId,
      ticker: position.ticker,
      selectedAmount: `${getTotalAmountForPosition(position, flowType)}`,
      selectedCostBasisMethod: position.selectedCostBasisMethod,
      sharesToTransfer:
        flowType === FlowTypes.INTERNAL_TRANSFER
          ? String(position.sharesToTransfer)
          : String(position.shareQuantity),
    };
    if (position.shownCostBasisMethod !== null) {
      payloadPosition['shownCostBasisMethod'] = position.shownCostBasisMethod;
    }
    if (position.percentOfHolding !== null) {
      payloadPosition['percentOfHolding'] = String(position.percentOfHolding);
    }

    if (flowType === FlowTypes.GIFTING) {
      payloadPosition.giftingMethod = position.giftingMethod;

      if (position.taxLots) {
        payloadPosition.taxLots = position.taxLots
          .filter((taxLot) => {
            const amount = getTaxLotsAmount(position.giftingMethod, taxLot);
            return amount > 0;
          })
          .map((taxLot) => {
            const giftingTaxLot: MXMTaxLot = {
              lotID: taxLot.lotID,
              quantity: getTaxLotsAmount(position.giftingMethod, taxLot),
            };
            return giftingTaxLot;
          });
      }
      if (position.additionalTaxLot) {
        payloadPosition.additionalTaxLotId = position.additionalTaxLot.lotID;
      }
    }
    return payloadPosition;
  });
};
